import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { X } from "lucide-react";

import { FOCUS_RING } from "../lib/focus";
import { HIT_AREA } from "../lib/target";
import { cn } from "../lib/utils";
import { Avatar, type AvatarShape } from "./avatar";
import { Icon, type IconComponent } from "./icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * v2 Chip + ChipGroup (redesign phase 5, #455). Storybook only until
 * implementation; see docs/design-library/04-implementation-notes.md.
 *
 * A chip is a TOKEN for an object or value: a machine, an ontology entity, an
 * ID, a scope, a selected filter value. A Badge is a static status label; a
 * selectable filter is a SegmentedControl (one value) or ToggleGroup
 * (several), never a chip.
 *
 * Look (break #115): 24px, 1px `--border`, transparent fill (the surface),
 * radius 6 like controls (a Badge is a radius-4 tint without border), Sora
 * 12/16 `--ink`. Neutral only, no tones. Optional leading icon 12
 * (`--ink-muted`) or Avatar xs. Long values truncate at 240px and show the
 * full text in a tooltip, only when actually cut.
 *
 * Three kinds: static (`<span>`), button (`onClick`), link (`asChild` with
 * the caller's `<a>` / router Link). Removable chips get a 20px "X" button
 * named "<label> entfernen". The frame is a `<span>`: the link or button and
 * the remove button are siblings in it, never nested. Delete or Backspace on
 * a focused part of a removable chip removes it.
 */

export const CHIP_FRAME =
  "inline-flex h-6 max-w-60 shrink-0 items-center rounded-md border border-border align-middle font-sans text-caption text-ink";

/** The interactive part: overlay hover, Button's offset focus outline. */
const CHIP_INTERACTIVE =
  "cursor-pointer hover:bg-hover-overlay data-hover:bg-hover-overlay active:bg-active-overlay " +
  FOCUS_RING;

const ChipGroupContext = React.createContext<{ willRemove: (chip: HTMLElement) => void } | null>(null);

export interface ChipProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children" | "onClick"> {
  /** The value. Also the remove button's name and the truncation tooltip. */
  label: string;
  /** Leading 12px icon. */
  icon?: IconComponent;
  /** Leading Avatar xs (decorative: the label names it). */
  avatar?: { name: string; src?: string; shape?: AvatarShape };
  /** Makes the chip a button. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Render the single child element (a link) as the chip. */
  asChild?: boolean;
  /** Only with `asChild`: the link element, without content; the chip fills it. */
  children?: React.ReactElement;
  /** Shows the remove button; Delete / Backspace also call it. */
  onRemove?: () => void;
  /** Default "<label> entfernen". */
  removeLabel?: string;
  /** IDs and numbers: `tabular-nums`. */
  numeric?: boolean;
  /** Decorative element after the label, inside the chip (a channel Badge). Never truncates; give it its own aria-hidden if the chip is named elsewhere. */
  trailing?: React.ReactNode;
}

const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { label, icon, avatar, onClick, asChild = false, children, onRemove, removeLabel, numeric = false, trailing, className, ...props },
  ref,
) {
  const frameRef = React.useRef<HTMLSpanElement>(null);
  React.useImperativeHandle(ref, () => frameRef.current as HTMLSpanElement);
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const group = React.useContext(ChipGroupContext);

  const remove = onRemove
    ? () => {
        if (frameRef.current) group?.willRemove(frameRef.current);
        onRemove();
      }
    : undefined;

  const onKeyDown = remove
    ? (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Delete" || event.key === "Backspace") {
          event.preventDefault();
          remove();
        }
      }
    : undefined;

  const interactive = asChild || onClick != null;
  const mainClass = cn(
    "flex h-full min-w-0 items-center gap-1 rounded-md",
    avatar ? "pl-0.5" : icon ? "pl-1.5" : "pl-2",
    onRemove ? "pr-1" : "pr-2",
    interactive && CHIP_INTERACTIVE,
  );

  const content = (
    <>
      {avatar ? (
        <Avatar name={avatar.name} src={avatar.src} shape={avatar.shape} size="xs" />
      ) : icon ? (
        <Icon icon={icon} size={12} className="text-ink-muted" />
      ) : null}
      <span ref={labelRef} className={cn("min-w-0 truncate", numeric && "tabular-nums")}>
        {label}
      </span>
      {trailing}
    </>
  );

  // asChild: the caller's link gets the content; Slot merges the classes and handlers onto it.
  const main = asChild && children ? (
    <Slot className={mainClass} onKeyDown={onKeyDown}>
      {React.cloneElement(children, {}, content)}
    </Slot>
  ) : onClick ? (
    <button type="button" className={mainClass} onClick={onClick} onKeyDown={onKeyDown}>
      {content}
    </button>
  ) : (
    <span className={mainClass}>{content}</span>
  );

  return (
    <span ref={frameRef} data-slot="chip" className={cn(CHIP_FRAME, className)} {...props}>
      {/* Only a cut label gets the tooltip. Not a description: the label is the name already. */}
      <Tooltip
        open={tooltipOpen}
        onOpenChange={(open) => {
          const el = labelRef.current;
          setTooltipOpen(open && !!el && el.scrollWidth > el.clientWidth);
        }}
      >
        <TooltipTrigger asChild aria-describedby={undefined}>
          {main}
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      {remove && (
        <button
          type="button"
          aria-label={removeLabel ?? `${label} entfernen`}
          onClick={remove}
          onKeyDown={onKeyDown}
          className={cn(
            "mr-0.5 inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-ink-muted",
            HIT_AREA,
            "hover:bg-hover-overlay hover:text-ink data-hover:bg-hover-overlay data-hover:text-ink active:bg-active-overlay",
            FOCUS_RING,
          )}
        >
          <Icon icon={X} size={12} />
        </button>
      )}
    </span>
  );
});

export interface ChipGroupLabels {
  /** The overflow chip's text. Default "+3". */
  more?: (hidden: number) => string;
  /** The overflow chip's name. Default "3 weitere anzeigen". */
  moreLabel?: (hidden: number) => string;
}

export interface ChipGroupProps extends Omit<React.HTMLAttributes<HTMLUListElement>, "children"> {
  children: React.ReactNode;
  /** Chips before the overflow chip; it expands the group in place. */
  max?: number;
  labels?: ChipGroupLabels;
  /** Replaces the overflow chip, e.g. a link to the full list. */
  renderOverflow?: (hidden: number) => React.ReactNode;
}

/**
 * Chips in a wrapping list (break #116), 4px apart, as `<ul role="list">`.
 * After `max` an overflow chip "+3" shows the rest in place (focus moves to
 * the first chip it revealed); `renderOverflow` replaces it.
 *
 * Focus after a remove: a chip tells the group before it calls `onRemove`;
 * after the next render the group focuses the chip now at that place (the
 * next one), else the previous one, else the list itself.
 */
const ChipGroup = React.forwardRef<HTMLUListElement, ChipGroupProps>(function ChipGroup(
  { children, max, labels, renderOverflow, className, ...props },
  ref,
) {
  const listRef = React.useRef<HTMLUListElement>(null);
  React.useImperativeHandle(ref, () => listRef.current as HTMLUListElement);
  const [expanded, setExpanded] = React.useState(false);
  const pendingFocus = React.useRef<number | null>(null);

  const items = React.Children.toArray(children).filter(React.isValidElement);
  const visible = max != null && !expanded ? items.slice(0, max) : items;
  const hidden = items.length - visible.length;

  const context = React.useMemo(
    () => ({
      willRemove: (chip: HTMLElement) => {
        const lis = Array.from(listRef.current?.children ?? []);
        const index = lis.findIndex((li) => li.contains(chip));
        if (index >= 0) pendingFocus.current = index;
      },
    }),
    [],
  );

  // No dependency list on purpose: it acts on the render after a remove (or an expand), whenever that comes.
  React.useLayoutEffect(() => {
    const index = pendingFocus.current;
    const list = listRef.current;
    if (index == null || !list) return;
    pendingFocus.current = null;
    const lis = Array.from(list.children) as HTMLElement[];
    const target = lis[index] ?? lis[index - 1];
    const focusable = target?.querySelector<HTMLElement>("a[href], button");
    (focusable ?? list).focus();
  });

  return (
    <ChipGroupContext.Provider value={context}>
      <ul
        ref={listRef}
        role="list"
        tabIndex={-1}
        className={cn("flex flex-wrap items-center gap-1 outline-hidden", className)}
        {...props}
      >
        {visible.map((item, i) => (
          <li key={item.key ?? i} className="flex min-w-0">
            {item}
          </li>
        ))}
        {hidden > 0 && (
          <li className="flex">
            {renderOverflow ? (
              renderOverflow(hidden)
            ) : (
              <button
                type="button"
                aria-label={labels?.moreLabel?.(hidden) ?? `${hidden} weitere anzeigen`}
                onClick={() => {
                  pendingFocus.current = visible.length;
                  setExpanded(true);
                }}
                className={cn(CHIP_FRAME, CHIP_INTERACTIVE, "px-2 tabular-nums")}
              >
                {labels?.more?.(hidden) ?? `+${hidden}`}
              </button>
            )}
          </li>
        )}
      </ul>
    </ChipGroupContext.Provider>
  );
});

export { Chip, ChipGroup };
